import { useEffect, useState } from 'react';
import { useAlertContext } from '../contexts/AlertContext';
import { useApplication } from '../services/application';
import { useTrans } from '../services/i18n';
import { AmplifyAuth } from '../services/amplifyAuth';

export default function usePhoneInput({ auth, updatePhone, onClose, updatePhoneVerified }) {
  const { trans } = useTrans();
  const application = useApplication();
  const { successAlert, errorAlert } = useAlertContext();
  const [sentCode, setSentCode] = useState(false);
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [newPhone, setPhone] = useState('');
  const [resendPending, setResendPending] = useState(false);

  const { phoneVerified, phone } = auth;

  const sendVerificationCode = async () => {
    const currentUser = await AmplifyAuth.currentAuthenticatedUser({
      bypassCache: true,
    });

    await AmplifyAuth.updateUserAttributes(currentUser, {
      phone_number: newPhone,
    });

    await AmplifyAuth.verifyCurrentUserAttribute('phone_number');
  };

  const handleSetPhone = async () => {
    setPending(true);

    if (!sentCode) {
      if (newPhone === phone && (phoneVerified === true || phoneVerified === 'true')) {
        successAlert(trans('phone-already-verified'));
        onClose();
        setPending(false);
        return;
      }

      try {
        await sendVerificationCode();

        setSentCode(true);
        updatePhone(newPhone);
        updatePhoneVerified(false);
        successAlert(trans('new-code-has-been-sent'));
      } catch (e) {
        errorAlert(e.message);
      } finally {
        setPending(false);
      }

      return;
    }

    try {
      await AmplifyAuth.verifyCurrentUserAttributeSubmit('phone_number', code);

      const res = await application.call('admin.profile.setPhone', {
        phone: newPhone,
      });

      if (res) {
        updatePhone(res.phone || newPhone);
        updatePhoneVerified(true);
        successAlert(trans('success-modified-phone'));
        onClose();
      }
    } catch (e) {
      updatePhoneVerified(false);
      errorAlert(e.message);
    } finally {
      setPending(false);
    }
  };

  const resendCode = async () => {
    setResendPending(true);

    return AmplifyAuth.verifyCurrentUserAttribute('phone_number')
      .then(() => {
        successAlert(trans('new-code-has-been-sent'));
      })
      .catch((e) => {
        errorAlert(e.message);
      })
      .finally(() => setResendPending(false));
  };

  useEffect(() => {
    if (phone) {
      setPhone(phone);
    }
  }, [phone]);

  return {
    phone,
    phoneVerified,
    sentCode,
    resendPending,
    code,
    pending,
    newPhone,
    resendCode,
    setCode,
    setPhone,
    handleSetPhone,
  };
}